// @system — Google Analytics & Google Tag Manager integration, consent-gated.
// Set VITE_GA_MEASUREMENT_ID and/or VITE_GTM_ID in client .env to enable.
// Renders nothing — just initializes tracking scripts, and only once the visitor has accepted
// all cookies (CookieConsentBanner / cookie-consent.js store the choice; useCookieConsent reads
// it and follows same-tab changes). Essential-only or no choice yet = no tag is loaded — GDPR
// Art. 7 and launch contract rule 19 (analytics behind consent). The provider a product runs is
// declared to crawlers and the launch check by <meta name="analytics-provider"> in index.html
// (brand.json `analytics.provider`, `none` when unset).
// trackEvent / trackPageView live in lib/@system/analytics.js.

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageView } from '@/app/lib/@system/analytics'
import { useCookieConsent } from '@/app/components/@system/CookieConsentBanner'

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID
const GTM_ID = import.meta.env.VITE_GTM_ID

function initGA() {
  if (!GA_MEASUREMENT_ID || window.__ga_initialized) return
  window.__ga_initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  const dataLayer = (window.dataLayer = window.dataLayer || [])
  function gtag() { dataLayer.push(arguments) }
  window.gtag = gtag
  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false })
}

function initGTM() {
  if (!GTM_ID || window.__gtm_initialized) return
  window.__gtm_initialized = true

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)
}

export default function Analytics() {
  const location = useLocation()
  const { consent } = useCookieConsent()
  const analyticsAllowed = consent === 'all'

  useEffect(() => {
    if (!analyticsAllowed) return
    initGA()
    initGTM()
  }, [analyticsAllowed])

  useEffect(() => {
    if (!analyticsAllowed) return
    trackPageView(location.pathname + location.search)
  }, [location, analyticsAllowed])

  return null
}
